/**
 * Deterministic identity → color + initials, shared by presence cursors, the
 * facepile, remote-selection tags, and chat attribution. The same id always
 * maps to the same color on every client, so a peer is one consistent color
 * everywhere and N users don't visibly collide (hues spread by golden angle).
 */

/** FNV-1a — small, fast, stable across runs/clients. */
function hashString(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/**
 * Stable identity color as an `oklch()` string. Fixed L/C tuned to read on both
 * light and dark chrome; hue spread by the golden angle so adjacent ids land far
 * apart on the wheel.
 */
export function colorFor(id: string): string {
  const hue = Math.round((hashString(id) * 137.508) % 360);
  return `oklch(0.68 0.17 ${hue})`;
}

/**
 * Up-to-2-char initials from a display name or an email. A name with a space
 * uses the first letter of its first two words ("Maya Rao" → "MR"); otherwise
 * the local-part / name supplies the first two alphanumerics
 * ("jmhudak@proton.me" → "JM"). Auth exposes only `email`, hence the email path.
 */
export function initialsFor(nameOrEmail: string): string {
  const src = nameOrEmail.trim();
  if (!src) return '?';
  const at = src.indexOf('@');
  const base = at > 0 ? src.slice(0, at) : src;
  const words = base.split(/[\s._-]+/).filter(Boolean);
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  return base.replace(/[^a-zA-Z0-9]/g, '').slice(0, 2).toUpperCase() || '?';
}

/** Display handle: the email local-part, or the name verbatim. */
export function handleFor(nameOrEmail: string): string {
  const at = nameOrEmail.indexOf('@');
  return at > 0 ? nameOrEmail.slice(0, at) : nameOrEmail;
}
